import type { CanalTicket, NovoTicket, Ticket } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { PortalClienteApi } from './portal-cliente.api.js';

const ROTULO_CANAL: Record<CanalTicket, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  site: 'Site',
};

/**
 * `<erp-solicitacao-form-modal>` — abre uma nova solicitação de suporte
 * a partir do Portal do Cliente. Mesmo padrão de
 * `suporte/ticket-form-modal.ts`, mas sem seletor de cliente: o
 * `clienteId` nunca é escolhido aqui, é sempre o da sessão do Portal
 * (resolvido no backend a partir do cookie, nunca aceito do corpo da
 * requisição).
 */
export class ErpSolicitacaoFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _portalApi: PortalClienteApi | null = null;
  private _aoSalvar: ((ticket: Ticket) => void) | null = null;

  private aberto = false;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set portalApi(valor: PortalClienteApi) {
    this._portalApi = valor;
  }

  set aoSalvar(valor: (ticket: Ticket) => void) {
    this._aoSalvar = valor;
  }

  abrir(): void {
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._portalApi || this.enviando) return;

    const canal = this.valorCampo('#canal') as CanalTicket;
    const assunto = this.valorCampo('#assunto').trim();
    const descricao = this.valorCampo('#descricao').trim();

    if (!assunto) {
      this.mensagemErro = 'assunto é obrigatório';
      this.render();
      return;
    }
    if (!descricao) {
      this.mensagemErro = 'descrição é obrigatória';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const dado: Omit<NovoTicket, 'clienteId'> = { canal, assunto, descricao };
      const ticket = await this._portalApi.abrirSolicitacao(dado);
      this.aberto = false;
      this._aoSalvar?.(ticket);
    } catch (erro) {
      this.mensagemErro =
        erro instanceof ApiError ? erro.message : 'não foi possível abrir a solicitação. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        textarea { resize: vertical; }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Nova solicitação</span>
        <form novalidate>
          <label>
            Canal
            <select id="canal">
              ${Object.entries(ROTULO_CANAL)
                .map(([valor, rotulo]) => `<option value="${valor}">${rotulo}</option>`)
                .join('')}
            </select>
          </label>
          <label>Assunto <input id="assunto" type="text" maxlength="200" /></label>
          <label>Descrição <textarea id="descricao" rows="4" maxlength="5000"></textarea></label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Enviar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-solicitacao-form-modal', ErpSolicitacaoFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-solicitacao-form-modal': ErpSolicitacaoFormModal;
  }
}
